import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { leadsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Phone, Mail, MapPin, MessageCircle, HelpCircle, FileText } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_xalawnsplus/artifacts/gh7eo36y_a0d14065-cb1f-449b-a4f6-38140797df8a.png";

export default function ContactPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    inquiry_type: 'curious',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.address) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      await leadsAPI.create(formData);
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        inquiry_type: 'curious',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inquiryOptions = [
    { value: 'curious', label: 'Just Curious', icon: HelpCircle, description: 'I want to learn more about your services' },
    { value: 'quote', label: 'Get a Quote', icon: FileText, description: 'I need pricing for my property' },
    { value: 'talk_options', label: "Let's Talk Options", icon: MessageCircle, description: 'I want to discuss my specific needs' }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="border-b border-[#262626] bg-black/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="XA's Lawns" className="h-12" />
            </Link>
            <Link to="/" className="text-gray-400 hover:text-white flex items-center gap-2 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Column - Info */}
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase mb-6">
              Get in <span className="text-lime-500">Touch</span>
            </h1>
            <p className="text-gray-400 text-lg mb-10">
              Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>

            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-lime-500/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-lime-500" />
                </div>
                <div>
                  <h4 className="font-heading text-lg font-bold text-white uppercase mb-1">Phone</h4>
                  <p className="text-gray-400">(318) 706-6876</p>
                  <p className="text-gray-500 text-sm">Mon-Sat 7am-7pm</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-gold-500/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-gold-500" />
                </div>
                <div>
                  <h4 className="font-heading text-lg font-bold text-white uppercase mb-1">Email</h4>
                  <p className="text-gray-400">contact@xaslawns.com</p>
                  <p className="text-gray-500 text-sm">We respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-lime-500/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-lime-500" />
                </div>
                <div>
                  <h4 className="font-heading text-lg font-bold text-white uppercase mb-1">Service Area</h4>
                  <p className="text-gray-400">Your Local Area & Surrounding</p>
                  <p className="text-gray-500 text-sm">Free estimates for all locations</p>
                </div>
              </div>
            </div>

            {/* Quick links */}
            <div className="card-dark p-6">
              <h4 className="font-heading text-lg font-bold text-white uppercase mb-4">Need a Quote Fast?</h4>
              <p className="text-gray-400 mb-4">
                Use our estimate form for faster service and get a detailed quote for your property.
              </p>
              <Link to="/estimate">
                <Button className="btn-secondary w-full" data-testid="contact-estimate-link">
                  Get Free Estimate
                </Button>
              </Link>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="card-dark p-8">
            <h2 className="font-heading text-2xl font-bold text-white uppercase mb-6">Send a Message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Inquiry Type */}
              <div>
                <Label className="text-white mb-3 block">What can we help you with?</Label>
                <RadioGroup 
                  value={formData.inquiry_type} 
                  onValueChange={(value) => setFormData({...formData, inquiry_type: value})}
                  className="space-y-3"
                >
                  {inquiryOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-all ${
                        formData.inquiry_type === option.value 
                          ? 'border-lime-500 bg-lime-500/10' 
                          : 'border-[#262626] hover:border-[#404040]'
                      }`}
                      data-testid={`inquiry-${option.value}`}
                    >
                      <RadioGroupItem value={option.value} className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <option.icon className={`w-4 h-4 ${formData.inquiry_type === option.value ? 'text-lime-500' : 'text-gray-400'}`} />
                          <span className={`font-medium ${formData.inquiry_type === option.value ? 'text-white' : 'text-gray-300'}`}>
                            {option.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              {/* Name */}
              <div>
                <Label htmlFor="name" className="text-white">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="input-dark mt-2"
                  placeholder="Your name"
                  required
                  data-testid="contact-name-input"
                />
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="text-white">Phone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="input-dark mt-2"
                  placeholder="(555) 123-4567"
                  required
                  data-testid="contact-phone-input"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-white">Email (Optional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="input-dark mt-2"
                  placeholder="your@email.com"
                  data-testid="contact-email-input"
                />
              </div>

              {/* Service Address */}
              <div>
                <Label htmlFor="address" className="text-white">Service Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="input-dark mt-2"
                  placeholder="123 Main St, City, State ZIP"
                  required
                  data-testid="contact-address-input"
                />
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="text-white">Message (Optional)</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="input-dark mt-2 min-h-[120px]"
                  placeholder="Tell us more about what you're looking for..."
                  data-testid="contact-message-input"
                />
              </div>

              <Button 
                type="submit" 
                className="btn-primary w-full"
                disabled={loading}
                data-testid="contact-submit-btn"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
